
import { Component, Mixins, Watch } from 'vue-property-decorator';
import DarkThemeInfoMixin from '~common-mixins/DarkThemeInfoMixin';
import UserRightsMixin from '~/mixins/UserRightsMixin';
import { HomesModule } from '~/store/modules/homes';
import { DevicesModule } from '~/store/modules/devices';
import { TreeNodeObject } from '~/interfaces/helper.interface';
import NetatmoIcon from '~/components/NetatmoIcon.vue';
import EliotTreeMenu from '~/components/Eliot/EliotTreeMenu.vue';
import { uuidv4 } from '~/helpers';
import Tree from './Tree.vue';
import { EliotModule } from '~/store/modules/eliot';
import { humanReadableField } from '~/filters/humanReadableField';
import { LayoutModule } from '~/store/modules/layout';

@Component({
    components: {
        Tree,
        NetatmoIcon,
        EliotTreeMenu,
    },
})
export default class TheTreeMenu extends Mixins(
    DarkThemeInfoMixin,
    UserRightsMixin,
) {
    openedHomeNodes: string[] = [];
    openedNotInAHomeNodes: string[] = [];
    homeNodes: TreeNodeObject[] = [];
    notInAHomeNode: TreeNodeObject[] = [];
    isOpen = true;

    get homes() {
        return HomesModule.homes;
    }

    get plants() {
        return EliotModule.plants;
    }

    get filter() {
        return LayoutModule.treeFilter;
    }

    set filter(filter: string) {
        LayoutModule.setTreeFilter(filter);
    }

    @Watch('homes', { immediate: true, deep: true })
    updateTreeNodes() {
        let homeNodes: TreeNodeObject[] = [];
        let notInAHomeNode: TreeNodeObject[] = [];

        homeNodes = this.createHomeNodes(homeNodes);
        ({ homeNodes, notInAHomeNode } = this.createGatewayNodes(
            homeNodes,
            notInAHomeNode,
        ));
        ({ homeNodes, notInAHomeNode } = this.createModuleNodes(
            homeNodes,
            notInAHomeNode,
        ));

        homeNodes = this.sortHomeNodes(homeNodes);

        this.homeNodes = homeNodes;
        this.notInAHomeNode = notInAHomeNode;

        this.initializeOpenedNodes();
    }

    createHomeNodes(homeNodes: TreeNodeObject[]) {
        const homeNodesCopy: TreeNodeObject[] = JSON.parse(
            JSON.stringify(homeNodes),
        );

        // create home nodes
        this.homes.forEach((home) => {
            homeNodesCopy.push({
                id: home.id,
                type: 'home',
                primaryInfo: home.name,
                tertiaryInfo: home.id,
                canBeActive: true,
                children: [],
            });
        });

        return homeNodesCopy;
    }

    createGatewayNodes(
        homeNodes: TreeNodeObject[],
        notInAHomeNode: TreeNodeObject[],
    ) {
        const homeNodesCopy: TreeNodeObject[] = JSON.parse(
            JSON.stringify(homeNodes),
        );
        const notInAHomeNodeCopy: TreeNodeObject[] = JSON.parse(
            JSON.stringify(notInAHomeNode),
        );

        // create gateway nodes and push them as children of their home node
        DevicesModule.gatewaysFiltered.forEach((gateway) => {
            const gatewayNode: TreeNodeObject = {
                // create random id for gateways that don't have an id
                id: gateway.id === '' ? 'no-id-' + uuidv4() : gateway.id,
                type: 'gateway',
                subType: gateway.typeToDisplay,
                homeId: gateway.homeId,
                primaryInfo: humanReadableField(
                    gateway.typeToDisplay,
                ),
                secondaryInfo: gateway.name,
                tertiaryInfo: gateway.id,
                canBeActive: gateway.id !== '',
                children: [],
            };
            // if gateway is in a home, find the home node and add the gateway node to its children
            if (gateway.homeId) {
                const parentNode = homeNodesCopy.find(
                    (node) => node.id === gateway.homeId,
                );
                if (parentNode) {
                    parentNode.children.push(gatewayNode);
                }
            } else {
                // else, if the "not in a home" node exists, add the gateway node to its children
                if (notInAHomeNodeCopy[0]) {
                    notInAHomeNodeCopy[0].children.push(gatewayNode);
                } else {
                    // else, create the "not in a home" node, with the gateway node as its first child
                    notInAHomeNodeCopy.push({
                        id: 'notinahome',
                        type: 'notinahome',
                        primaryInfo: 'Not in a home',
                        canBeActive: false,
                        children: [gatewayNode],
                    });
                }
            }
        });

        return { homeNodes: homeNodesCopy, notInAHomeNode: notInAHomeNodeCopy };
    }

    createModuleNodes(
        homeNodes: TreeNodeObject[],
        notInAHomeNode: TreeNodeObject[],
    ) {
        const homeNodesCopy: TreeNodeObject[] = JSON.parse(
            JSON.stringify(homeNodes),
        );
        const notInAHomeNodeCopy: TreeNodeObject[] = JSON.parse(
            JSON.stringify(notInAHomeNode),
        );

        // create module nodes and push them as children of their gateway node
        DevicesModule.modulesFiltered
            // order with wired modules first
            .sort((modA) => {
                return modA.isWired ? -1 : 1;
            })
            // group by type
            .sort((modA, modB) => {
                return modA.type > modB.type ? 1 : -1;
            })
            .forEach((mod) => {
                if (!mod.shouldShowDevice) {
                    return;
                }
                let gatewayNode;
                // if module is in a home
                if (mod.homeId) {
                    // find the gateway node of the module in the homes nodes
                    const homeNode = homeNodesCopy.find(
                        (node) => node.id === mod.homeId,
                    );
                    if (homeNode) {
                        gatewayNode = homeNode.children.find(
                            (node) => node.id === mod.bridge,
                        );
                    }
                } else {
                    // else find the gateway node in the "not in a home" node
                    gatewayNode = notInAHomeNodeCopy.find(
                        (node) => node.id === mod.bridge,
                    );
                }
                // if the parent gateway node was found add the module node to its children
                if (gatewayNode) {
                    const moduleNode: TreeNodeObject = {
                        id: mod.id,
                        type: 'module',
                        subType: mod.typeToDisplay,
                        homeId: mod.homeId,
                        gatewayId: mod.bridge,
                        primaryInfo: humanReadableField(
                            mod.typeToDisplay,
                        ),
                        secondaryInfo: mod.name,
                        tertiaryInfo: mod.id,
                        canBeActive: mod.id !== '',
                        children: [],
                    };
                    gatewayNode.children.push(moduleNode);
                }
            });

        return { homeNodes: homeNodesCopy, notInAHomeNode: notInAHomeNodeCopy };
    }

    sortHomeNodes(homeNodes: TreeNodeObject[]) {
        const homeNodesCopy: TreeNodeObject[] = JSON.parse(
            JSON.stringify(homeNodes),
        );

        // sort empty homes
        homeNodesCopy.sort((nodeA: TreeNodeObject, nodeB: TreeNodeObject) => {
            return nodeB.children.length === 0 ? -1 : 1;
        });

        return homeNodesCopy;
    }

    initializeOpenedNodes() {
        this.openedHomeNodes = [
            ...HomesModule.homes.map((home) => home.id),
            DevicesModule.currentGatewayId,
            DevicesModule.currentModuleId,
        ] as string[];

        this.openedNotInAHomeNodes = ['notinahome'];
    }

    updateOpenedNodes(openedNodes: string[], isInAHome: boolean) {
        if (isInAHome) {
            this.openedHomeNodes = openedNodes;
        } else {
            this.openedNotInAHomeNodes = openedNodes;
        }
    }
}
